// The board a dev refresh comes back to. **Local only.**
//
// A refresh on `npm start` used to drop whatever was being played: the AI
// client back to its menu, the human one onto a fresh board. The client
// switch already survives a remount — the store is the root's and the shell's
// launch state is on a shelf (`useKept`) — so a refresh is the same remount
// with both written to `localStorage` on the way out and read back on boot.
//
// Written on `pagehide`, never per command: a refresh is the one moment it is
// for, and encoding the whole state on every move is `runSave`'s price for a
// player's only copy, not a developer's convenience.
//
// **The log is preferred and the snapshot is the fallback**, `loadRun`'s
// trade: a log that still replays to the saved hash keeps undo and history;
// one the rules have moved under (an edit since the refresh, which is the
// point of a dev server) lands the snapshot alone. Neither verifying, the
// save is dropped and the board starts fresh.

import type { GameMode } from "src/api/intents";
import { hashState } from "src/engine/rules/hash";
import { encode } from "./codec";
import type { GameStore } from "./GameStore";
import { reviveSnapshot } from "./runSave";
import { verifies } from "./saveFormat";

const KEY = "factory-chess:dev-board";

interface DevBoard {
  state: unknown;
  hash: number;
  log: readonly unknown[];
  mode?: GameMode;
  /** The client's own launch state — opaque here, the shelf's to read. */
  shell?: Record<string, unknown>;
}

const storage = (): Storage | null => {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
};

/**
 * Is a refresh resumed at all? A dev server, not a browser under automation
 * (the smokes reload and expect the menu), not a room (the server replays a
 * room's log on rejoin). `?fresh` (main.tsx) skips one load.
 */
export function devResumeOn(): boolean {
  if (!import.meta.env.DEV || typeof window === "undefined") return false;
  if (navigator.webdriver) return false;
  return !new URLSearchParams(window.location.search).has("room");
}

/** Write the store, its mode and the shell's state. Never throws. */
export function saveDevBoard(store: GameStore, shell?: Record<string, unknown>): void {
  const box = storage();
  if (!box) return;
  try {
    const { state, log } = store.getSnapshot();
    const board: DevBoard = { state: encode(state), hash: hashState(state), log, mode: store.modeOf(), shell };
    box.setItem(KEY, JSON.stringify(board));
  } catch {
    // Quota or an unencodable shelf value: the next refresh starts fresh.
  }
}

/**
 * Put the saved board back into the store and hand back the shell's state,
 * or null when there is nothing usable (the save is dropped then).
 */
export function loadDevBoard(store: GameStore): Record<string, unknown> | null {
  const box = storage();
  const raw = box?.getItem(KEY);
  if (!box || !raw) return null;
  try {
    const board = JSON.parse(raw) as DevBoard;
    const log = board.log as Parameters<typeof verifies>[0];
    if (log.length > 0 && verifies(log, board.hash)) store.load(log);
    else {
      const state = reviveSnapshot(board.state, board.hash);
      if (!state) throw new Error("dev board no longer decodes");
      store.restore(state);
    }
    store.setMode(board.mode);
    return board.shell ?? {};
  } catch (error) {
    console.info("dev resume: starting fresh —", error instanceof Error ? error.message : error);
    box.removeItem(KEY);
    return null;
  }
}
